import { faTrophy, faUsers, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { TagNew as Tag } from "components/tagNew";
import moment from "moment";
import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  computeEventBands,
  EVENT_TYPE_COLORS,
  EVENT_TYPE_ICON_NAMES,
  filterTimelineEvents,
  IDashboardEvent,
  IEventBand,
  LABEL_ROW_HEIGHT,
  MIN_LABEL_WIDTH_FOR_NAME_WITH_ICON,
  TDashboardEventType,
} from "./dashboardEventTimeline";

interface IProps {
  hourLabels: string[];
  events?: Array<IDashboardEvent>;
}

const EVENT_ICONS: Record<NonNullable<(typeof EVENT_TYPE_ICON_NAMES)[TDashboardEventType]>, IconDefinition> = {
  faUsers,
  faTrophy,
};

function formatEventTimeRange(startTime: string, endTime: string): string {
  return `${moment(startTime, "HH:mm:ss").format("LT")} - ${moment(endTime, "HH:mm:ss").format("LT")}`;
}

function EventBandLabel(props: { band: IEventBand; bandRef: React.RefObject<HTMLDivElement> }) {
  const { band, bandRef } = props;
  const [showName, setShowName] = useState(true);
  const iconName = EVENT_TYPE_ICON_NAMES[band.eventType];
  const icon = iconName ? EVENT_ICONS[iconName] : null;

  useEffect(() => {
    if (!icon) {
      setShowName(true);
      return;
    }

    const element = bandRef.current;
    if (!element) {
      return;
    }

    const updateNameVisibility = () => {
      const width = element.getBoundingClientRect().width;
      setShowName(width >= MIN_LABEL_WIDTH_FOR_NAME_WITH_ICON);
    };

    updateNameVisibility();

    const observer = new ResizeObserver(updateNameVisibility);
    observer.observe(element);

    return () => observer.disconnect();
  }, [bandRef, icon, band.eventName]);

  return (
    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]}
      overlay={
        <Tooltip id={`player-volume-event-band-${band.id}`}>
          <div>{band.eventName}</div>
          <div>{formatEventTimeRange(band.startTime, band.endTime)}</div>
        </Tooltip>
      }
    >
      <div
        className="player-booking-graph-event-band-label"
        style={
          {
            top: band.row > 0 ? `${4 + band.row * LABEL_ROW_HEIGHT}px` : undefined,
            "--event-label-color": band.color,
          } as CSSProperties
        }
      >
        <Tag
          className={classNames("player-booking-graph-event-band-tag", {
            "player-booking-graph-event-band-tag--icon-only": Boolean(icon && !showName),
          })}
          icon={icon ? <FontAwesomeIcon icon={icon} /> : undefined}
        >
          {showName ? band.eventName : ""}
        </Tag>
      </div>
    </OverlayTrigger>
  );
}

function EventBand(props: { band: IEventBand }) {
  const { band } = props;
  const bandRef = useRef<HTMLDivElement>(null);

  return (
      <div
        ref={bandRef}
        className={`player-booking-graph-event-band player-booking-graph-event-band--${band.eventType}`}
        style={{
          left: `${band.leftPercent}%`,
          width: `${band.widthPercent}%`,
          borderColor: band.color,
          backgroundColor: band.fillColor,
        }}
      >
        <EventBandLabel band={band} bandRef={bandRef} />
      </div>
  );
}

export function EventBands(props: IProps) {
  const { hourLabels, events } = props;

  const timelineEvents = useMemo(() => filterTimelineEvents(events), [events]);
  const eventBands = useMemo(() => computeEventBands(hourLabels, timelineEvents), [hourLabels, timelineEvents]);

  if (!events || !eventBands.length) {
    return null;
  }

  return (
    <div className="player-booking-graph-event-bands">
      {eventBands.map(band => (
        <EventBand key={band.id} band={band} />
      ))}
    </div>
  );
}

export default function EventTimeline(props: IProps) {
  return <EventBands {...props} />;
}

export function EventTypeLegend(props: { types: TDashboardEventType[] }) {
  const { types } = props;

  if (!types.length) {
    return null;
  }

  const labels: Record<TDashboardEventType, string> = {
    league: "League",
    tournament: "Tournament",
    shotgun: "Shotgun",
  };

  return (
    <div className="player-booking-graph-event-legend">
      {types.map(type => (
        <p key={type} className="player-booking-graph-event-legend-item">
          <span
            className="player-booking-graph-event-legend-dot"
            style={{ backgroundColor: EVENT_TYPE_COLORS[type] }}
          />
          {labels[type]}
        </p>
      ))}
    </div>
  );
}
